import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './KnowledgeArticleEdit.css';
import { KnowledgeArticle, KnowledgeArticleCategory } from '../types';
import { knowledgeBaseApi } from '../services/api';
import { API_URL } from '../config';
import useAlert from '../hooks/useAlert';
import { IMAGE_ACCEPT, validateKbImageFile } from '../utils/uploadValidation';
import { sanitizeRichTextHtml } from '../utils/richTextSanitizer';

const KnowledgeArticleEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  // Categoria já persistida, para só chamar a rota de categoria quando ela mudar.
  const [savedCategoryId, setSavedCategoryId] = useState<number | null>(null);
  const [categories, setCategories] = useState<KnowledgeArticleCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingArticle, setLoadingArticle] = useState(isEdit);
  const navigate = useNavigate();
  const { showAlert, AlertComponent } = useAlert();
  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    fetchCategories();
    if (isEdit && id) {
      fetchArticle();
    }
  }, [id, isEdit]);

  const fetchCategories = async () => {
    try {
      const data = await knowledgeBaseApi.getCategories();
      setCategories(data);
      if (data.length > 0 && !categoryId) {
        setCategoryId(data[0].id);
      }
    } catch (error: any) {
      console.error('Erro ao carregar categorias:', error);
      showAlert({ title: 'Erro', message: 'Erro ao carregar categorias', variant: 'danger' });
    }
  };

  const fetchArticle = async () => {
    try {
      setLoadingArticle(true);
      const article = await knowledgeBaseApi.getArticle(Number(id));
      if (article.can_edit === false) {
        showAlert({
          title: 'Acesso negado',
          message: 'Você não tem permissão para editar este artigo.',
          variant: 'danger',
        });
        navigate(`/knowledge-base/${id}`);
        return;
      }
      setTitle(article.title);
      setBody(article.body);
      setCategoryId(article.category_id);
      setSavedCategoryId(article.category_id);
    } catch (error: any) {
      console.error('Erro ao carregar artigo:', error);
      showAlert({ title: 'Erro', message: 'Erro ao carregar artigo', variant: 'danger' });
    } finally {
      setLoadingArticle(false);
    }
  };

  const handleImageUpload = React.useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', IMAGE_ACCEPT);
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fileError = validateKbImageFile(file);
      if (fileError) {
        showAlert({ title: 'Arquivo inválido', message: fileError, variant: 'warning' });
        return;
      }

      try {
        const result = await knowledgeBaseApi.uploadImage(file);
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection();
          const index = range ? range.index : 0;
          const imageSrc = /^(https?:)?\/\//i.test(result.url) || result.url.startsWith('data:')
            ? result.url
            : `${API_URL}${result.url.startsWith('/') ? '' : '/'}${result.url}`;
          quill.insertEmbed(index, 'image', imageSrc);
        }
      } catch (error: any) {
        console.error('Erro ao fazer upload da imagem:', error);
        showAlert({ title: 'Erro', message: 'Erro ao fazer upload da imagem', variant: 'danger' });
      }
    };
  }, [showAlert]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const sanitizedBody = sanitizeRichTextHtml(body, {
      allowedTags: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'div', 'span',
        'strong', 'b', 'em', 'i', 'u', 's',
        'ul', 'ol', 'li',
        'blockquote', 'pre', 'code',
        'a', 'img',
      ],
      allowImageSrc: (src) => {
        const value = src.trim().toLowerCase();
        return value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:');
      },
    });

    if (!title.trim() || !sanitizedBody.trim() || !categoryId) {
      showAlert({ title: 'Aviso', message: 'Preencha todos os campos obrigatórios', variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      if (isEdit && id) {
        const articleId = Number(id);
        const nextCategoryId = Number(categoryId);
        // A categoria vai primeiro: se ela falhar (categoria removida por outra
        // pessoa, por exemplo), o texto ainda não foi gravado e o usuário pode
        // tentar de novo sem perder o que escreveu.
        if (savedCategoryId === null || nextCategoryId !== savedCategoryId) {
          await knowledgeBaseApi.updateArticleCategory(articleId, nextCategoryId);
          setSavedCategoryId(nextCategoryId);
        }
        await knowledgeBaseApi.updateArticle(articleId, {
          title,
          body: sanitizedBody,
        });
        showAlert({ title: 'Sucesso', message: 'Artigo atualizado com sucesso', variant: 'success' });
      } else {
        await knowledgeBaseApi.createArticle({
          title,
          body: sanitizedBody,
          category_id: Number(categoryId),
        });
        showAlert({ title: 'Sucesso', message: 'Artigo criado com sucesso', variant: 'success' });
      }
      navigate('/knowledge-base');
    } catch (error: any) {
      console.error('Erro ao salvar artigo:', error);
      showAlert({ title: 'Erro', message: 'Erro ao salvar artigo', variant: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const modules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: handleImageUpload,
      },
    },
  }), [handleImageUpload]);

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'align',
    'link',
    'image',
  ];

  if (loadingArticle) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AlertComponent />
      <Row className="mb-3">
        <Col>
          <h2>{isEdit ? 'Editar Artigo' : 'Criar Artigo'}</h2>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" onClick={() => navigate('/knowledge-base')}>
            Cancelar
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Título *</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="Digite o título do artigo"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Categoria *</Form.Label>
              <Form.Select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
                required
              >
                <option value="">Selecione uma categoria</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Corpo do Artigo *</Form.Label>
              <div className="quill-wrapper csp-wave1-kb-quill-wrapper">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={body}
                  onChange={setBody}
                  modules={modules}
                  formats={formats}
                  placeholder="Digite o conteúdo do artigo aqui..."
                />
              </div>
              <Form.Text className="text-muted">
                Use o botão de imagem na barra de ferramentas para fazer upload de imagens.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/knowledge-base')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default KnowledgeArticleEdit;
